import type { GuideEntry, GuideStack } from '@elara/client-core/states/guide-stack';

import { Fragment } from 'react';

import { cn, pluralize } from '@elara/client-core/lib/utils';

import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '#ui/dropdown-menu.js';

/**
 * How many earlier guides show by name before the rest collapse into an ellipsis menu: two on a wide
 * panel, one on a phone-width one, where a second name would leave each too short to read.
 */
const VISIBLE_CRUMBS = { wide: 2, narrow: 1 } as const;

const TYPE_LABELS: Record<GuideEntry['type'], string> = {
  destination: 'Destination',
  hotel: 'Hotel',
  room: 'Room',
  flight: 'Flight',
  place: 'Place',
};

/** A guide's breadcrumb label: its own title once it has rendered, else what kind of guide it is. */
function guideLabel(entry: GuideEntry | undefined): string {
  if (!entry) return '';
  return entry.title || TYPE_LABELS[entry.type];
}

type Crumb = { kind: 'guide'; index: number } | { kind: 'collapsed'; indexes: number[] };

/** The guides before the current one; a longer path shows a menu of the earlier ones, then the last `visible`. */
function crumbsFor(stack: GuideStack, visible: number): Crumb[] {
  const path = Array.from({ length: stack.index }, (_, index) => index);
  if (path.length <= visible) return path.map((index) => ({ kind: 'guide', index }));
  return [
    { kind: 'collapsed', indexes: path.slice(0, -visible) },
    ...path.slice(-visible).map((index): Crumb => ({ kind: 'guide', index })),
  ];
}

/**
 * The way back through the guide panel: each guide before the one showing, jumping back to it. The
 * current guide is left out — its own title heads the content below, and the panel fades it in after
 * the trailing slash once that heading scrolls away. Guides ahead are reached with
 * Forward, like a browser's history. Renders nothing at the start of the path.
 */
export function GuideBreadcrumbs({
  stack,
  onSelect,
  narrow = false,
  className,
}: {
  stack: GuideStack;
  onSelect: (index: number) => void;
  /** A phone-width panel: one earlier guide by name instead of two. */
  narrow?: boolean;
  className?: string;
}) {
  const crumbs = crumbsFor(stack, narrow ? VISIBLE_CRUMBS.narrow : VISIBLE_CRUMBS.wide);
  if (crumbs.length === 0) return null;
  return (
    <nav aria-label="Guide path" className={cn('min-w-0', className)}>
      <ol className="flex min-w-0 items-center gap-1 text-sm">
        {crumbs.map((crumb) => (
          <Fragment key={crumb.kind === 'guide' ? stack.entries[crumb.index]!.key : 'collapsed'}>
            <li className={crumb.kind === 'collapsed' ? 'shrink-0' : 'min-w-0 shrink'}>
              {crumb.kind === 'collapsed' ? (
                <DropdownMenu>
                  <DropdownMenuTrigger
                    className="cursor-pointer rounded px-1 text-muted-foreground hover:text-foreground"
                    aria-label={`${crumb.indexes.length} more ${pluralize(crumb.indexes.length, 'guide')}`}
                  >
                    …
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="start">
                    {crumb.indexes.map((index) => (
                      <DropdownMenuItem key={stack.entries[index]!.key} onSelect={() => onSelect(index)}>
                        {guideLabel(stack.entries[index])}
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
              ) : (
                <button
                  type="button"
                  onClick={() => onSelect(crumb.index)}
                  title={guideLabel(stack.entries[crumb.index])}
                  className="block max-w-full cursor-pointer truncate text-muted-foreground hover:text-foreground"
                >
                  {guideLabel(stack.entries[crumb.index])}
                </button>
              )}
            </li>
            {/* After every guide, the last too: the path leads on into the guide showing below. */}
            <li aria-hidden className="shrink-0 text-muted-foreground/60">
              /
            </li>
          </Fragment>
        ))}
      </ol>
    </nav>
  );
}
